import React, { useState, useEffect, useRef } from 'react';
import { Bell, Package, TrendingUp, Gift, Info, Coins, ShoppingBag, X, Check, Trash2 } from 'lucide-react';
import { useNotifications, formatRelativeTime, NOTIFICATION_TYPES } from '../contexts/NotificationContext';

/**
 * NotificationBell — دکمه زنگ + dropdown اعلان‌ها
 *
 * استفاده: <NotificationBell />
 *
 * — وقتی dropdown باز می‌شه، خودکار همه اعلان‌ها read می‌شن و badge قرمز محو می‌شه
 * — کلیک خارج از dropdown → بسته می‌شه
 * — حالت خالی: پیام مناسب نمایش داده می‌شه
 */

// ─── متادیتای انواع اعلان ─────────────────────────────────────────
const TYPE_META = {
  [NOTIFICATION_TYPES.ORDER]: {
    icon: Package,
    iconColor: 'text-blue-600',
    bgColor: 'bg-blue-100',
  },
  [NOTIFICATION_TYPES.PRICE]: {
    icon: TrendingUp,
    iconColor: 'text-green-600',
    bgColor: 'bg-green-100',
  },
  [NOTIFICATION_TYPES.PROMOTION]: {
    icon: Gift,
    iconColor: 'text-purple-600',
    bgColor: 'bg-purple-100',
  },
  [NOTIFICATION_TYPES.GOLD_BANK]: {
    icon: Coins,
    iconColor: 'text-amber-600',
    bgColor: 'bg-amber-100',
  },
  [NOTIFICATION_TYPES.CART]: {
    icon: ShoppingBag,
    iconColor: 'text-pink-600',
    bgColor: 'bg-pink-100',
  },
  [NOTIFICATION_TYPES.SYSTEM]: {
    icon: Info,
    iconColor: 'text-gray-600',
    bgColor: 'bg-gray-100',
  },
};

const getTypeMeta = (type) => TYPE_META[type] || TYPE_META[NOTIFICATION_TYPES.SYSTEM];

export const NotificationBell = ({ buttonClassName = '' }) => {
  const {
    notifications,
    unreadCount,
    markAllAsRead,
    markAsRead,
    removeNotification,
    clearAll,
  } = useNotifications();

  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const justOpenedRef = useRef(false);

  // ─── بستن با کلیک خارج ───────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (!open) return;
      // وقتی الان باز شد، اولین click خود dropdown رو نادیده بگیر
      if (justOpenedRef.current) {
        justOpenedRef.current = false;
        return;
      }
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // ─── با باز شدن، همه رو read کن ──────────────────────────────
  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      justOpenedRef.current = true;
      // کمی تأخیر تا کاربر بادج قرمز رو ببینه که گم شد
      setTimeout(() => markAllAsRead(), 800);
    }
  };

  return (
    <div className="relative" ref={wrapRef}>
      {/* ─── دکمه زنگ ─────────────────────────────────────────── */}
      <button
        type="button"
        onClick={handleToggle}
        className={`p-2 hover:bg-amber-50 rounded-full transition-colors relative ${buttonClassName}`}
        aria-label={`اعلان‌ها (${unreadCount} خوانده‌نشده)`}
      >
        <Bell className={`w-6 h-6 ${unreadCount > 0 ? 'text-amber-600' : 'text-gray-700'}`} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center animate-pulse">
            {unreadCount > 99 ? '۹۹+' : unreadCount}
          </span>
        )}
      </button>

      {/* ─── Dropdown ─────────────────────────────────────────── */}
      {open && (
        <div className="absolute left-0 mt-2 w-[360px] max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2">
          {/* هدر */}
          <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-l from-amber-50/50 to-white flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-amber-600" />
              <h3 className="font-bold text-gray-800 text-sm">اعلان‌ها</h3>
              {notifications.length > 0 && (
                <span className="text-xs text-gray-500">({notifications.length})</span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {notifications.length > 0 && (
                <button
                  type="button"
                  onClick={clearAll}
                  className="p-1.5 hover:bg-red-50 rounded text-gray-500 hover:text-red-600 transition-colors"
                  title="حذف همه"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-1.5 hover:bg-gray-100 rounded text-gray-500 transition-colors"
                title="بستن"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* محتوا */}
          <div className="max-h-[400px] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-12 px-4 text-center">
                <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-gray-50 flex items-center justify-center">
                  <Bell className="w-6 h-6 text-gray-300" />
                </div>
                <p className="text-sm text-gray-600 font-medium">هنوز اعلانی ندارید</p>
                <p className="text-xs text-gray-400 mt-1">وقتی اتفاق جدیدی بیفته اینجا نمایش داده می‌شه</p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-50">
                {notifications.map(notif => {
                  const meta = getTypeMeta(notif.type);
                  const Icon = meta.icon;
                  return (
                    <li
                      key={notif.id}
                      className={`group relative px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer ${
                        !notif.read ? 'bg-amber-50/40' : ''
                      }`}
                      onClick={() => markAsRead(notif.id)}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${meta.bgColor}`}>
                          <Icon className={`w-4 h-4 ${meta.iconColor}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-sm text-gray-800 truncate">
                            {notif.title}
                          </h4>
                          {notif.message && (
                            <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">
                              {notif.message}
                            </p>
                          )}
                          <p className="text-[11px] text-gray-400 mt-1">
                            {formatRelativeTime(notif.timestamp)}
                          </p>
                        </div>
                        {!notif.read && (
                          <span className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0" />
                        )}
                        {/* دکمه حذف فقط در hover */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeNotification(notif.id);
                          }}
                          className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-100 text-gray-400 hover:text-red-600 transition-all"
                          title="حذف این اعلان"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* فوتر */}
          {notifications.length > 0 && unreadCount > 0 && (
            <div className="px-4 py-2 border-t border-gray-100 bg-gray-50">
              <button
                type="button"
                onClick={markAllAsRead}
                className="w-full flex items-center justify-center gap-1.5 text-xs font-medium text-amber-700 hover:text-amber-800 py-1"
              >
                <Check className="w-3.5 h-3.5" />
                علامت‌گذاری همه به‌عنوان خوانده‌شده
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;